/**
 * Wendet eine Zahlungsziel-Option auf eine Bestellung an (Spec 2026-09-07, Abschnitt 4).
 *
 * Der Server prueft die Freigabe, ersetzt die Ratenzeilen und schreibt die Momentaufnahme in einer
 * Transaktion. 400 steht fuer eine nicht freigegebene Option, 409 fuer bereits fixierte oder
 * bezahlte Raten; beide Meldungen koennen unveraendert im Toast erscheinen.
 *
 * Seit dem Nachtrag 01.10.2026 ("Nach Plan oder individuell") kommen zwei Plan-Endpunkte dazu:
 * `POST .../payment-plan/individualize` und `PUT .../payment-plan`. Beide antworten in der Form
 * `ZahlungsplanErgebnis` (Plan 2026-10-02, E10).
 */
import { anfrage } from "./basis.ts";
import { alsRate } from "./zahlungsraten.ts";
import type { OrderPayment } from "../lib/types.ts";
import type { PlanRateEingabe } from "../lib/zahlungsplanEditor.ts";

/**
 * Was `PUT .../payment-terms` mit den Raten tut (Ticket G). `automatisch` (Standard des Servers) erneuert
 * sie nur, wenn der Plan einer Vorlage folgt und keine Rate fixiert oder bezahlt ist, und haelt sonst nur
 * das Zahlungsziel fest. `ersetzen` ist "Zurueck auf Vorlage": immer erneuern, bei fixierter oder
 * bezahlter Rate 409.
 */
export type ZahlungszielRaten = "automatisch" | "ersetzen";

export interface ZahlungszielStandardErgebnis {
  payments: OrderPayment[];
  paymentTerms: string;
  paymentTermsTemplateId: string;
  /** Herkunft des Plans (`order_extras.payment_plan_source`); NULL = kein Plan. */
  paymentPlanSource: string | null;
  extrasRowVersion: number;
  /**
   * Merker "Zahlungsziel gewechselt" (`order_extras.zahlungsziel_gewechselt_am`, Migration 082) nach diesem Aufruf: ISO-Zeitpunkt
   * des ersten Wechsels, `null` = nie gewechselt. Fehlt bei aelteren Servern.
   */
  zahlungszielGewechseltAm?: string | null;
  /** `false`: nur das Zahlungsziel wurde festgehalten, die Raten sind unveraendert. Fehlt bei aelteren Servern. */
  ratenErsetzt?: boolean;
}

/** Antwort von `individualize` und `PUT payment-plan` (E10). */
export interface ZahlungsplanErgebnis {
  payments: OrderPayment[];
  extrasRowVersion: number;
  paymentPlanSource: string | null;
  paymentTermsTemplateId: string | null;
  paymentTerms: string | null;
}

export interface ZahlstatusResetErgebnis {
  orderId: string;
  changed: boolean;
  rowVersion: number | null;
  /** Fehlt normalerweise; sonst z. B. "Zentrale Vorlage unbrauchbar – Bestellung bleibt ohne Zahlungsplan". */
  warnung?: string;
}

/** `raten` fehlt im Body, wenn es nicht angegeben ist: dann gilt der Standard des Servers (`automatisch`). */
export function zahlungszielSetzen(
  bestellungId: string,
  templateId: string,
  raten?: ZahlungszielRaten,
): Promise<ZahlungszielStandardErgebnis> {
  return anfrage<ZahlungszielStandardErgebnis>(`/api/orders/${encodeURIComponent(bestellungId)}/payment-terms`, {
    methode: "PUT",
    koerper: raten === undefined ? { templateId } : { templateId, raten },
  });
}

/** Die Raten der Antwort tragen `dueDate`/`paidAt`; die Oberflaeche liest `due`/`paidDate` (siehe `alsRate`). */
function mitUebersetztenRaten(roh: ZahlungsplanErgebnis): ZahlungsplanErgebnis {
  return { ...roh, payments: (roh.payments ?? []).map((p) => alsRate(p) as OrderPayment) };
}

/**
 * "Individuell anpassen": setzt die Herkunft auf `individuell`, Raten und Vorlagen-ID bleiben
 * unveraendert. `rowVersion` ist die von `order_extras`.
 */
export async function planIndividualisieren(
  bestellungId: string,
  rowVersion: number,
): Promise<ZahlungsplanErgebnis> {
  const antwort = await anfrage<ZahlungsplanErgebnis>(
    `/api/orders/${encodeURIComponent(bestellungId)}/payment-plan/individualize`,
    { methode: "POST", koerper: { rowVersion } },
  );
  return mitUebersetztenRaten(antwort);
}

/**
 * Speichert den ganzen individuellen Plan atomar (`PUT /api/orders/:id/payment-plan`). `raten` in
 * der Reihenfolge des Plans; bestehende Raten mit `id` und `rowVersion`. 400 `{error, feld?}` bei
 * einem fachlichen Fehler, 409 `{error, current?}` bei einem Konflikt.
 */
export async function planSpeichern(
  bestellungId: string,
  rowVersion: number,
  raten: readonly PlanRateEingabe[],
): Promise<ZahlungsplanErgebnis> {
  const antwort = await anfrage<ZahlungsplanErgebnis>(
    `/api/orders/${encodeURIComponent(bestellungId)}/payment-plan`,
    { methode: "PUT", koerper: { rowVersion, raten } },
  );
  return mitUebersetztenRaten(antwort);
}

export function zahlstatusZuruecksetzen(bestellungId: string): Promise<ZahlstatusResetErgebnis> {
  return anfrage<ZahlstatusResetErgebnis>(
    `/api/orders/${encodeURIComponent(bestellungId)}/payment-state/reset`,
    { methode: "POST" },
  );
}
